/**
 * Adapter functions to extract scorecard data from match_data
 * Handles various data shapes and computes from deliveries if needed
 */

interface Player {
  id: string;
  name: string;
  runs: number;
  balls: number;
  fours: number;
  sixes: number;
  isOut?: boolean;
  dismissal?: string | null;
  dismissalDetails?: {
    type?: string;
    bowlerName?: string;
    fielderName?: string;
  };
  isCaptain?: boolean;
  isKeeper?: boolean;
  outsCount?: number;
}

interface Delivery {
  runs: number;
  isLegal: boolean;
  isWicket: boolean;
  batterId?: string;
  bowlerId?: string;
  dismissal?: string | null;
  display?: string;
  extraType?: string | null;
}

interface Innings {
  battingTeam: "A" | "B";
  bowlingTeam?: "A" | "B";
  deliveries: Delivery[];
  totalRuns: number;
  wickets: number;
  totalBalls: number;
  extras?: {
    wides: number;
    noBalls: number;
    byes: number;
    legByes: number;
  };
}

export interface BattingRow {
  name: string;
  runs: number;
  balls: number;
  fours: number;
  sixes: number;
  strikeRate: string;
  howOut: string;
  isCaptain?: boolean;
  isKeeper?: boolean;
  outsCount?: number;
}

export interface BowlingRow {
  name: string;
  overs: string;
  maidens: number;
  runs: number;
  wickets: number;
  economy: string;
  wides?: number;
  noballs?: number;
  isCaptain?: boolean;
  isKeeper?: boolean;
}

export interface FallOfWicket {
  order: number;
  score: number;
  wicket: number;
  over: string;
  batterName: string;
  dismissal: string;
}

/**
 * Check if an innings has meaningful activity
 */
export function hasActivityInInnings(innings: any): boolean {
  if (!innings) return false;

  return (
    (innings.deliveries && innings.deliveries.length > 0) ||
    innings.totalRuns > 0 ||
    innings.wickets > 0 ||
    innings.totalBalls > 0
  );
}

/**
 * Get only active innings (filter out ghost/empty innings)
 */
export function getActiveInnings(matchData: any): any[] {
  if (!matchData || !matchData.innings || !Array.isArray(matchData.innings)) {
    return [];
  }

  const currentIdx = typeof matchData.currentInnings === 'number' ? matchData.currentInnings : 0;

  // Filter innings with real activity, but always include the currently-batting
  // innings (even at 0/0) so a freshly-started 2nd innings is visible publicly.
  const activeInnings = matchData.innings.filter((inn: any, index: number) => {
    const hasActivity = hasActivityInInnings(inn);
    const isCurrent = index === currentIdx;

    // Deduplicate: keep only the first occurrence of each batting team that
    // either has activity OR is the current innings.
    const firstIndexOfTeam = matchData.innings.findIndex(
      (i: any, j: number) =>
        i.battingTeam === inn.battingTeam &&
        (hasActivityInInnings(i) || j === currentIdx)
    );

    return (hasActivity || isCurrent) && firstIndexOfTeam === index;
  });

  return activeInnings;
}

/**
 * Extract batting rows from innings and player data
 */
export function getBattingRows(
  innings: Innings,
  battingTeamPlayers?: Player[],
  format?: string
): BattingRow[] {
  if (!battingTeamPlayers || battingTeamPlayers.length === 0) {
    return [];
  }

  const isIndoor = format === "INDOOR";

  // Filter and map players with activity
  const rows = battingTeamPlayers
    .filter((p: Player) => p.balls > 0 || p.runs > 0 || p.isOut || (isIndoor && (p.outsCount || 0) > 0))
    .map((p: Player) => {
      // Calculate strike rate
      const sr = p.balls > 0 ? ((p.runs / p.balls) * 100).toFixed(1) : "—";

      // Format dismissal
      let howOut = "not out";
      if (p.isOut) {
        if (p.dismissalDetails) {
          const details = p.dismissalDetails;
          if (details.type === "Bowled") {
            howOut = `b ${details.bowlerName || "?"}`;
          } else if (details.type === "Caught") {
            howOut = `c ${details.fielderName || "?"} b ${details.bowlerName || "?"}`;
          } else if (details.type === "LBW") {
            howOut = `lbw b ${details.bowlerName || "?"}`;
          } else if (details.type === "Stumped") {
            howOut = `st ${details.fielderName || "?"} b ${details.bowlerName || "?"}`;
          } else if (details.type === "Run Out") {
            howOut = `run out${details.fielderName ? ` (${details.fielderName})` : ""}`;
          } else {
            howOut = details.type || p.dismissal || "out";
          }
        } else {
          howOut = p.dismissal || "out";
        }
      } else if (isIndoor && (p.outsCount || 0) > 0) {
        howOut = `not out (re-entered ×${p.outsCount})`;
      }

      return {
        name: p.name,
        runs: p.runs,
        balls: p.balls,
        fours: p.fours,
        sixes: p.sixes,
        strikeRate: sr,
        howOut,
        isCaptain: p.isCaptain,
        isKeeper: p.isKeeper,
        outsCount: p.outsCount,
      };
    });

  return rows;
}

/**
 * Extract bowling rows from innings deliveries
 */
export function getBowlingRows(
  innings: Innings,
  bowlingTeamPlayers?: Player[],
  ballsPerOver = 6
): BowlingRow[] {
  if (!innings.deliveries || innings.deliveries.length === 0) {
    return [];
  }

  // Build a map of bowler stats from deliveries
  const bowlerMap = new Map<
    string,
    {
      name: string;
      balls: number;
      runs: number;
      wickets: number;
      wides: number;
      noballs: number;
      maidens: number;
      ballsInCurrentOver: number;
      runsInCurrentOver: number;
      isCaptain?: boolean;
      isKeeper?: boolean;
    }
  >();

  const getPlayerName = (id?: string) => {
    if (!id) return "Unknown";
    const player = bowlingTeamPlayers?.find((p) => p.id === id);
    return player?.name || "Unknown";
  };

  const getPlayerData = (id?: string) => {
    if (!id) return null;
    return bowlingTeamPlayers?.find((p) => p.id === id) || null;
  };

  innings.deliveries.forEach((d: Delivery) => {
    const bowlerId = d.bowlerId || "_unknown";
    if (bowlerId === "_unknown") return;

    if (!bowlerMap.has(bowlerId)) {
      const player = getPlayerData(bowlerId);
      bowlerMap.set(bowlerId, {
        name: getPlayerName(bowlerId),
        balls: 0,
        runs: 0,
        wickets: 0,
        wides: 0,
        noballs: 0,
        maidens: 0,
        ballsInCurrentOver: 0,
        runsInCurrentOver: 0,
        isCaptain: player?.isCaptain,
        isKeeper: player?.isKeeper,
      });
    }

    const bowler = bowlerMap.get(bowlerId)!;

    // Add runs conceded
    bowler.runs += d.runs;

    // Track all runs in the over for maiden calculation (wides/no-balls disqualify maidens)
    bowler.runsInCurrentOver += d.runs;

    // Add legal ball and track runs-per-over for maiden calculation
    if (d.isLegal) {
      bowler.balls += 1;
      bowler.ballsInCurrentOver += 1;
      if (bowler.ballsInCurrentOver === ballsPerOver) {
        if (bowler.runsInCurrentOver === 0) bowler.maidens += 1;
        bowler.ballsInCurrentOver = 0;
        bowler.runsInCurrentOver = 0;
      }
    }

    // Add wicket (only if legal delivery, excluding Run Out which is not credited to bowler)
    if (d.isWicket && d.isLegal && d.dismissalType !== "Run Out") {
      bowler.wickets += 1;
    }

    // Track wides and noballs
    if (!d.isLegal && d.display) {
      if (d.display.startsWith("Wd")) {
        bowler.wides += 1;
      } else if (d.display.startsWith("Nb")) {
        bowler.noballs += 1;
      }
    }
  });

  // Convert map to array and format
  const rows = Array.from(bowlerMap.values())
    .filter((b) => b.balls > 0) // Only show bowlers who have bowled
    .map((b) => {
      const overs = Math.floor(b.balls / ballsPerOver);
      const balls = b.balls % ballsPerOver;
      const oversText = balls > 0 ? `${overs}.${balls}` : `${overs}`;
      const economy =
        b.balls > 0 ? ((b.runs / (b.balls / ballsPerOver)).toFixed(2)) : "—";

      return {
        name: b.name,
        overs: oversText,
        maidens: b.maidens,
        runs: b.runs,
        wickets: b.wickets,
        economy,
        wides: b.wides,
        noballs: b.noballs,
        isCaptain: b.isCaptain,
        isKeeper: b.isKeeper,
      };
    })
    .sort((a, b) => {
      // Sort by wickets desc, then economy asc
      if (a.wickets !== b.wickets) {
        return b.wickets - a.wickets;
      }
      return parseFloat(a.economy) - parseFloat(b.economy);
    });

  return rows;
}

/**
 * Extract fall of wickets from innings deliveries
 */
export function getFallOfWickets(
  innings: Innings,
  battingTeamPlayers?: Player[],
  ballsPerOver = 6,
  format?: string
): FallOfWicket[] {
  if (!innings.deliveries || innings.deliveries.length === 0) return [];

  const isIndoor = format === "INDOOR";
  const result: FallOfWicket[] = [];
  let cumulativeRuns = 0;
  let legalBalls = 0;
  let wicketCount = 0;

  for (const d of innings.deliveries) {
    cumulativeRuns += d.runs;
    if (d.isLegal) legalBalls++;

    if (d.isWicket && d.isLegal) {
      wicketCount++;
      if (isIndoor) cumulativeRuns -= 3;
      const batter = battingTeamPlayers?.find(p => p.id === d.batterId);
      const overNum = Math.floor(legalBalls / ballsPerOver);
      const ballInOver = legalBalls % ballsPerOver;
      const overText = ballInOver > 0 ? `${overNum}.${ballInOver}` : `${overNum}`;
      result.push({
        order: wicketCount,
        score: cumulativeRuns,
        wicket: wicketCount,
        over: overText,
        batterName: batter?.name || "Unknown",
        dismissal: d.dismissal || "out",
      });
    }
  }

  return result;
}

/**
 * Extract runs per over data for graphing
 */
export function getRunsPerOver(innings: Innings): number[] {
  if (!innings.deliveries || innings.deliveries.length === 0) {
    return [];
  }

  const runsPerOver: number[] = [];
  let currentOverRuns = 0;
  let ballsInOver = 0;

  innings.deliveries.forEach((d: Delivery) => {
    if (d.isLegal) {
      currentOverRuns += d.runs;
      ballsInOver += 1;

      if (ballsInOver === 6) {
        runsPerOver.push(currentOverRuns);
        currentOverRuns = 0;
        ballsInOver = 0;
      }
    }
  });

  // Add incomplete over if there are balls
  if (ballsInOver > 0) {
    runsPerOver.push(currentOverRuns);
  }

  return runsPerOver;
}
